import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Przekazuje dane zalogowanego klienta do widgetu Tidio i synchronizuje
 * jego ostatnie zamówienia z Tidio (przez edge function `tidio-sync-orders`),
 * dzięki czemu w rozmowie widać nr zamówienia i link do szczegółów w CMS.
 */
export default function TidioIdentify() {
  const { user, loading } = useAuth();
  const syncedFor = useRef<string | null>(null);

  useEffect(() => {
    if (loading) return;
    const w = window as any;

    if (!user) {
      syncedFor.current = null;
      return;
    }
    if (syncedFor.current === user.id) return;
    syncedFor.current = user.id;

    let cancelled = false;

    (async () => {
      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name, phone")
        .eq("user_id", user.id)
        .maybeSingle<{ full_name: string | null; phone: string | null }>();

      if (cancelled) return;

      const visitor = {
        distinct_id: user.id,
        email: user.email ?? "",
        name: profile?.full_name || user.user_metadata?.full_name || user.email || "",
        phone: profile?.phone || "",
      };

      // Ustaw dane odwiedzającego w widgecie (przed lub po załadowaniu skryptu)
      w.tidioIdentify = visitor;
      const applyVisitor = () => {
        try {
          w.tidioChatApi?.setVisitorData?.(visitor);
        } catch {
          /* no-op */
        }
      };
      if (w.tidioChatApi) applyVisitor();
      else document.addEventListener("tidioChat-ready", applyVisitor, { once: true });

      // Wyślij ostatnie zamówienia + linki do CMS na konto Tidio
      try {
        const { data, error } = await supabase.functions.invoke("tidio-sync-orders", { body: {} });
        if (error) return;
        const summary: string | undefined = (data as any)?.summary;
        if (summary && w.tidioChatApi?.addVisitorTags) {
          try {
            w.tidioChatApi.addVisitorTags((data as any)?.tags ?? []);
          } catch {
            /* no-op */
          }
        }
      } catch {
        /* cicho — czat działa też bez synchronizacji */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, loading]);

  return null;
}
